import { useCandidateProfile } from 'candidate_profile_redesign/store';
import { queryKeys } from './queryKeys';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  fetchFraudReport,
  requestFraudReport,
  retryFailedFraudReport,
  restoreFraudReport,
  resolveFraudReport,
  fetchFraudResolvedReasons,
} from '../api/fraud_report';

export const useFraudReport = () => {
  const {
    state: {
      currentUser: {
        permissions: { canViewFraudReports },
      },
    },
    paths: { fraudReportPath },
  } = useCandidateProfile();

  return useQuery({
    queryKey: queryKeys.fraudReport,
    queryFn: () => fetchFraudReport(fraudReportPath),
    staleTime: 2000,
    retry: false,
    enabled: canViewFraudReports,
    refetchOnReconnect: false,
    refetchOnWindowFocus: false,
    refetchInterval: (query) => {
      const fraudReport = query.state.data;

      // Refetch every 5 seconds if the report is pending
      return fraudReport?.status === 'pending' ? 5000 : false;
    },
  });
};

export const useFetchFraudResolvedReasons = ({
  enabled,
}: {
  enabled: boolean;
}) => {
  const {
    state: {
      currentUser: {
        permissions: { canViewFraudReports },
      },
    },
    paths: { fraudResolvedReasonsPath },
  } = useCandidateProfile();

  return useQuery({
    queryKey: queryKeys.fraudResolvedReasons,
    queryFn: () => fetchFraudResolvedReasons(fraudResolvedReasonsPath),
    staleTime: 60000,
    retry: false,
    enabled: canViewFraudReports && enabled,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
};

export const useRequestFraudReport = () => {
  const {
    paths: { fraudReportPath },
  } = useCandidateProfile();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => requestFraudReport(fraudReportPath),
    onSuccess: (fraudReport) => {
      // Sets query data to be a 'pending' fraud report
      queryClient.setQueryData(queryKeys.fraudReport, fraudReport);
    },
  });
};

export const useRetryFailedFraudReport = () => {
  const {
    paths: { fraudReportPath },
  } = useCandidateProfile();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => retryFailedFraudReport(fraudReportPath),
    onSuccess: (fraudReport) => {
      // Sets query data to be a 'pending' fraud report
      queryClient.setQueryData(queryKeys.fraudReport, fraudReport);
    },
  });
};

export const useRestoreFraudReport = () => {
  const {
    paths: { restoreFraudReportPath },
  } = useCandidateProfile();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: () => restoreFraudReport(restoreFraudReportPath),
    onSuccess: (fraudReport) => {
      queryClient.setQueryData(queryKeys.fraudReport, fraudReport);
    },
  });
};

export const useResolveFraudReport = () => {
  const {
    paths: { resolveFraudReportPath },
  } = useCandidateProfile();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      resolved_reason_ids,
      other_text,
    }: {
      resolved_reason_ids: number[];
      other_text?: string | null;
    }) =>
      resolveFraudReport(
        resolveFraudReportPath,
        resolved_reason_ids,
        other_text
      ),
    onSuccess: (fraudReport) => {
      queryClient.setQueryData(queryKeys.fraudReport, fraudReport);
    },
  });
};
